import type { BrandAlias } from '@jotta/config/brand'

////////////////////////////////////////////////////////////
/////////////////////// TYPES //////////////////////////////
////////////////////////////////////////////////////////////
type FontSizesThemeUI = [
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
]

type FontSizesTailwind = Record<
  'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl',
  string
>

////////////////////////////////////////////////////////////
/////////////////////// CONFIG //////////////////////////////
////////////////////////////////////////////////////////////

const fontConfig: Record<
  BrandAlias,
  {
    customFont?: string
    customHeading?: string
    fontSizes?: FontSizesThemeUI
  }
> = {
  test: {},
  jottacloud: {},
  elkjop: {},
  elgiganten: {},
  gigantti: {},
  elko: {},
  mediamarkt: {
    customFont: 'SourceSansPro',
  },
  saturn: {
    customFont: 'SourceSansPro',
  },
  tele2: {
    customFont: 'Tele2Sans',
  },
  teliasky: {
    customFont: 'TeliaSans',
  },
  teliacloud: {
    customFont: 'TeliaSans',
  },
  tsky: {},
  onlime: {
    customHeading: 'Coolvetica',
  },
  obos: {
    customFont: 'OBOS',
  },
  halebop: {
    customFont: 'halebopmedium',
    fontSizes: [12, 13, 15, 16, 18, 20, 24, 48, 64],
  },
  phonero: {},
  letsgo: {},
}

////////////////////////////////////////////////////////////
/////////////////////// FONTS //////////////////////////////
////////////////////////////////////////////////////////////

const standardFonts = [
  'Inter',
  'system-ui',
  '-apple-system',
  'BlinkMacSystemFont',
  'Segoe UI',
  'Roboto',
  'Helvetica Neue',
  'Arial',
  'sans-serif',
]

// https://drafts.csswg.org/css-fonts-3/#generic-font-families
const genericFonts = ['serif', 'sans-serif', 'cursive', 'fantasy', 'monospace']

export const countryFlagEmojiPolyfill = 'Twemoji Country Flags'

const resolveFonts = (customFont?: string) => {
  return [countryFlagEmojiPolyfill, customFont, ...standardFonts]
    .filter(font => font)
    .map(font => (font && genericFonts.includes(font) ? font : `"${font}"`))
    .join(',')
}

export const resolveFontFamily = (brandAlias: BrandAlias) => {
  const customBody = fontConfig[brandAlias].customFont
  const customHeading = fontConfig[brandAlias].customHeading
  return {
    body: resolveFonts(customBody),
    heading: resolveFonts(customHeading || customBody),
    monospace: 'Menlo, monospace',
  }
}

////////////////////////////////////////////////////////////
///////////////////// FONT SIZES ///////////////////////////
////////////////////////////////////////////////////////////

const standardFontSizes: FontSizesThemeUI = [
  12, // xs
  13, // sm
  15, // base
  16, // lg
  19, // xl
  24, // 2xl
  32, // 3xl
  48, // 4xl
  64, // 5xl
]

export const resolveThemeUIFontSizes = (
  brandAlias: BrandAlias,
): FontSizesThemeUI => {
  const fontSizes = fontConfig[brandAlias].fontSizes || standardFontSizes
  return fontSizes
}

export const resolveTailwindFontSizes = (
  brandAlias: BrandAlias,
): FontSizesTailwind => {
  const fontSizes = fontConfig[brandAlias].fontSizes || standardFontSizes

  const keys = [
    'xs',
    'sm',
    'base',
    'lg',
    'xl',
    '2xl',
    '3xl',
    '4xl',
    '5xl',
  ] as const

  const remSizes = fontSizes.map(size => `${(size / 16).toFixed(4)}rem`)

  return keys.reduce((acc, key, index) => {
    acc[key] = remSizes[index]
    return acc
  }, {} as FontSizesTailwind)
}
